import { useCallback } from 'react';
import { Id } from 'react-toastify';
import {
  AudioFileFormat,
  ScriptFormat,
} from '../../../server/graphql/__generated.types';
import { useDownloadIndividualClipsMutation } from './operation.generated';
import performDownload from './performDownload';
import { useEventsContext } from '../../context/events/EventsContext';
import { EventName } from '../../services/EventTrackingService';
import getElapsedTimeMs from '../../util/getElapsedTimeMs';
import { useAppSelector } from '../../store/hooks';
import { getDriveTypeFromName } from '../Drives/utils';
import { toast, updateToast } from '../Toaster';
import { usePromptsContext } from '../../context/prompts/PromptsContext';
import handleEntitlementDownloadError from './handleEntitlementDownloadError';

export interface ActorRelatedMetadata {
  actorId?: number;
  actorVariantId?: number;
  actorVariantType?: string;
  actorName?: string;
  ttsVersion?: string;
}

const useDownloadClips = () => {
  const [, dispatchPrompt] = usePromptsContext();
  const [download] = useDownloadIndividualClipsMutation();
  const [eventTracking] = useEventsContext();
  const { currentDrive } = useAppSelector(appState => appState.drives);

  const downloadClips = useCallback(
    async (inputs: {
      clipIds: string[];
      audioFileFormat: AudioFileFormat;
      actorRelatedMetadata?: ActorRelatedMetadata;
      includeScriptFormats?: Array<ScriptFormat>;
      setDownloadError?: (downloadError: string) => void;
    }) => {
      const {
        clipIds,
        audioFileFormat,
        actorRelatedMetadata,
        includeScriptFormats,
        setDownloadError,
      } = inputs;
      const startTime = performance.now();
      const errorMessage = 'Failed to download clips';
      let toastId: Id | undefined;
      try {
        const clipsPlural = clipIds.length > 0 ? 'clips' : 'clip';
        toastId = toast({
          message: `Downloading ${clipIds?.length} ${clipsPlural}`,
          type: 'loading',
        });
        await download({
          variables: {
            inputs: {
              audioFileFormat,
              clipIds,
              clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              includeScriptFormats,
            },
          },
          update: (_proxy, { data }) => {
            if (!data?.downloadClips.success) {
              const errorMsg = data?.downloadClips.message ?? errorMessage;
              const errorCode = data?.downloadClips.code;

              handleEntitlementDownloadError({
                errorCode,
                toastId,
                errorMessage: errorMsg,
                dispatchPrompt,
              });

              if (
                errorCode === 'clip_download_limit_exceeded' &&
                setDownloadError
              ) {
                setDownloadError(errorMsg);
              }

              eventTracking.track({
                type: EventName.ClipDownload,
                data: {
                  clipIds,
                  audioFormat: audioFileFormat,
                  success: false,
                  elapsedTimeMs: getElapsedTimeMs(startTime),
                  clipsCount: clipIds.length,
                  error: errorMsg,
                  includeScriptFormats: includeScriptFormats ?? [],
                  driveType: currentDrive
                    ? getDriveTypeFromName(currentDrive)
                    : undefined,
                  ...actorRelatedMetadata,
                },
              });
            } else {
              const { data: downloadData } = data.downloadClips;

              eventTracking.track({
                type: EventName.ClipDownload,
                data: {
                  clipIds,
                  audioFormat: audioFileFormat,
                  success: true,
                  elapsedTimeMs: getElapsedTimeMs(startTime),
                  clipsCount: clipIds.length,
                  combinedTextLength:
                    downloadData?.downloadMetricsMetadata.combinedTextLength ??
                    0,
                  numberOfClipsJustConverted:
                    downloadData?.downloadMetricsMetadata
                      .numberOfClipsJustConverted ?? 0,
                  zipFileAlreadyExisted:
                    downloadData?.downloadMetricsMetadata
                      .zipFileAlreadyExisted ?? undefined,
                  includeScriptFormats: includeScriptFormats ?? [],
                  driveType: currentDrive
                    ? getDriveTypeFromName(currentDrive)
                    : undefined,
                  ...actorRelatedMetadata,
                },
              });

              performDownload({
                fileFormat: downloadData!.fileFormat,
                friendlyFileName: downloadData!.friendlyFileName,
                signedURL: downloadData!.signedURL,
              });

              updateToast(toastId ?? '', {
                message: 'Successfully downloaded clips',
                type: 'success',
              });
            }
          },
        });
      } catch (error) {
        updateToast(toastId ?? '', {
          message: 'Failed to download clips',
          type: 'error',
        });
      }
    },
    [currentDrive, dispatchPrompt, download, eventTracking]
  );
  return downloadClips;
};

export default useDownloadClips;
